import React, { ReactNode, memo, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import PlayerControlsBaseFastForwardLimit from './player-controls-scrubber-base-fast-forward-limit';
import useInterval from '@smart-tv/hooks/use-interval';
import testIds from '@smart-tv/e2e/shared/variables';
import disableFastForwardCatchup from '../utils/disable-fastforward-catchup';
import useStream from '../../_hooks/use-stream';
import Slider from './slider';
import ProgressbarDisplayTime from './progressbar-time-value';
import { setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { useSkipButtons } from '../player-controls-navigation/skip-buttons';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { playerBtnsFocusHistoryAtom } from '../player-controls-navigation/use-player-controls-navigation';
import { getProgressPercentage, ScrubberProgress } from './player-controls-scrubber-utils';
import styles from './player-controls-scrubber.module.scss';

const FAST_FORWARD_LIMIT_UPDATE_INTERVAL = 1000;

interface Props {
  children?: ReactNode | ReactNode[];
  progress: ScrubberProgress;
  onChange: (value: number) => void;
  onChangeCommitted: (value: number) => void;
}

export const isSliderFocusedAtom = atom(false);
export const isScrubberDraggingAtom = atom(false);
export const dragSkipPositionAtom = atom<number>(0);

export const PlayerControlsScrubberBase = (props: Props) => {
  const { onChangeCommitted, children, onChange } = props;

  const {
    duration,
    progressPercentage = 0,
    progressSeconds = 0,
    liveProgressPercentage = 0,
    startTime,
    endTime
  } = props.progress;

  const sliderRef = useRef<HTMLDivElement>(null);

  const setIsSliderFocused = useSetAtom(isSliderFocusedAtom);
  const setIsScrubberDragging = useSetAtom(isScrubberDraggingAtom);
  const setDragSkipPosition = useSetAtom(dragSkipPositionAtom);

  const [isDragging, setIsDragging] = useState(false);

  const playerBtnsFocusHistory = useAtomValue(playerBtnsFocusHistoryAtom);

  const { skip } = useSkipButtons();

  const { stream, fastForwardLimitSeconds, setFastForwardLimitSeconds } = useStream();
  const trickmodes = stream ? stream.trickmodes : undefined;

  const fastForwardLimitPercentage = getProgressPercentage(fastForwardLimitSeconds, duration);

  const preventFastForward = useCallback(
    (value: number) => {
      return disableFastForwardCatchup(trickmodes) && value > fastForwardLimitPercentage;
    },
    [trickmodes, fastForwardLimitPercentage]
  );

  const handleChange = useCallback(
    (value: number) => {
      if (preventFastForward(value)) return;

      onChange?.(value);
      setDragSkipPosition((value / 100) * duration);
    },
    [onChange, preventFastForward, duration, setDragSkipPosition]
  );

  const handleChangeCommitted = useCallback(
    (value: number) => {
      setIsScrubberDragging(false);
      disableFastForwardCatchup(trickmodes)
        ? preventFastForward(value)
          ? onChangeCommitted?.(fastForwardLimitPercentage)
          : onChangeCommitted?.(value)
        : onChangeCommitted?.(value);
    },
    [
      onChangeCommitted,
      preventFastForward,
      setIsScrubberDragging,
      fastForwardLimitPercentage,
      trickmodes
    ]
  );

  // The limit is reset by closing the stream so on mount it only ever moves forward
  const handleUpdateFastForwardValues = useCallback(() => {
    if (!stream || !disableFastForwardCatchup(trickmodes)) return;

    if (progressSeconds > fastForwardLimitSeconds) {
      setFastForwardLimitSeconds(Math.round(progressSeconds));
    }
  }, [stream, trickmodes, fastForwardLimitSeconds, progressSeconds, setFastForwardLimitSeconds]);

  useInterval(handleUpdateFastForwardValues, FAST_FORWARD_LIMIT_UPDATE_INTERVAL);

  const arrowPressHandler = React.useCallback(
    (direction: string) => {
      if (direction === 'left') {
        skip(false);
      } else if (direction === 'right') {
        skip(true);
      } else if (direction === 'down') {
        setFocus(playerBtnsFocusHistory);
      }

      return true;
    },
    [playerBtnsFocusHistory, skip]
  );

  const { ref, focused } = useFocusable({
    onArrowPress: arrowPressHandler
  });

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsScrubberDragging(false);
    };
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [isDragging, setIsScrubberDragging]);

  const isActive = focused || isDragging;

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
    setIsScrubberDragging(true);
  }, [setIsScrubberDragging]);

  useEffect(() => {
    setIsSliderFocused(isActive);
  }, [isActive, setIsSliderFocused]);

  const disableFastForward = disableFastForwardCatchup(trickmodes);
  const validatedLiveProgress = liveProgressPercentage < 100 ? liveProgressPercentage : 100;

  const sliderClassNames = {
    bar: clsx(styles['player-controls-scrubber-base__slider-bar'], {
      [styles['player-controls-scrubber-base__slider-bar--focused']]: isActive
    }),
    track: clsx(styles['player-controls-scrubber-base__slider-track'], {
      [styles['player-controls-scrubber-base__slider-track--focused']]: isActive
    }),
    handle: clsx(styles['player-controls-scrubber-base__slider-handle'], {
      [styles['player-controls-scrubber-base__slider-handle--focused']]: isActive
    }),
    range: clsx(styles['player-controls-scrubber-base__slider-range'], {
      [styles['player-controls-scrubber-base__slider-range--focused']]: isActive,
      [styles['player-controls-scrubber-base__slider-range--disable-fast-forward']]:
        disableFastForward,
      [styles['player-controls-scrubber-base__slider-range--live-complete']]:
        validatedLiveProgress >= 100
    })
  };

  return (
    <div className={styles['player-controls-scrubber-base']} ref={ref}>
      <ProgressbarDisplayTime
        isPlacedLeft={true}
        progressSeconds={progressSeconds}
        duration={duration}
        startTime={startTime}
        endTime={endTime}
      />
      <div
        ref={sliderRef}
        className={styles['player-controls-scrubber-base__inner']}
        onMouseDown={handleMouseDown}
        data-interactive-control
      >
        <Slider
          color="success"
          rounded={false}
          onChange={handleChange}
          progressPercentage={progressPercentage}
          onChangeCommitted={handleChangeCommitted}
          data-testid={testIds.player.playerControls.scrubber}
          liveProgressPercentage={validatedLiveProgress}
          classNames={sliderClassNames}
        />

        <PlayerControlsBaseFastForwardLimit
          fastForwardLimitProgress={fastForwardLimitPercentage}
          disableFastForward={disableFastForward}
        />

        {children}
      </div>
      <ProgressbarDisplayTime
        isPlacedLeft={false}
        progressSeconds={progressSeconds}
        duration={duration}
        startTime={startTime}
        endTime={endTime}
      />
    </div>
  );
};

export default memo(PlayerControlsScrubberBase);
